import React from 'react';
import Modal from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { buildSetRoutingDestinationInput, SetRoutingDestinationInput } from 'src/models/setRoutingDestination';
import { setRoutingDestination } from 'src/store/common-slices/setRoutingDestinationSlice';
import { AlertLevels } from 'src/models/alert-model';
import { UIAlertState } from 'src/store/common-slices/uiSlice';

const EUROSORT_DESTINATION = 'EUROSORT';

interface AssignRoutingModalStateProps {
    uiAlertState: UIAlertState;
    toteId: string;
    currentToteDestination: string | null;
    scannedItemDestinations: string[];
    warehouseId: string;
    associate: string;
    locale: string;
}

interface AssignRoutingModalDispatchProps {
    setRoutingDestination: (input: SetRoutingDestinationInput) => Promise<any>;
}

interface AssignRoutingModalOwnProps {
    onClose: () => void;
    onAssignComplete: () => void;
}

type Props = AssignRoutingModalStateProps & AssignRoutingModalDispatchProps & AssignRoutingModalOwnProps;

interface State {
    selectedDestination: string;
    submitting: boolean;
    hasSubmitted: boolean;
}

class AssignRoutingModal extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            selectedDestination: '',
            submitting: false,
            hasSubmitted: false
        };
    }

    private getDestinationOptions = (): string[] => {
        const options = [this.props.currentToteDestination, ...this.props.scannedItemDestinations, EUROSORT_DESTINATION]
            .filter((destination): destination is string => !!destination);
        return Array.from(new Set(options));
    }

    private onSelectDestination = (destination: string) => {
        this.setState({ selectedDestination: destination });
    }

    private onConfirm = async () => {
        this.setState({ submitting: true, hasSubmitted: true });
        try {
            const result = await this.props.setRoutingDestination(buildSetRoutingDestinationInput(
                this.props.toteId, this.props.warehouseId, this.state.selectedDestination,
                this.props.associate, this.props.locale));

            if (setRoutingDestination.fulfilled.match(result)) {
                this.props.onAssignComplete();
                this.props.onClose();
            }
            // On failure, the modal stays open so the associate can see the error below and retry.
        } finally {
            this.setState({ submitting: false });
        }
    }

    private onCancel = () => {
        this.props.onClose();
    }

    public render() {
        return (
            <Modal title={AppStrings.ASSIGN_ROUTING_MODAL_TITLE} open={true}
                   onClose={this.state.submitting ? undefined : this.onCancel}
                   width={'60vw'} data-testid="assign-routing-modal">
                <Column spacingInset="400">
                    <Select
                        value={this.state.selectedDestination}
                        onChange={this.onSelectDestination}
                        placeholder={AppStrings.ASSIGN_ROUTING_SELECT_PLACEHOLDER}
                        data-testid="assign-routing-destination-select"
                    >
                        {this.getDestinationOptions().map(destination => (
                            <SelectOption label={destination} value={destination} key={destination} />
                        ))}
                    </Select>
                    {this.state.hasSubmitted && this.props.uiAlertState.alertLevel === AlertLevels.ERROR &&
                        <Alert type="error" size="medium" data-testid="assign-routing-error">
                            {this.props.uiAlertState.alertMessageString}
                        </Alert>
                    }
                    <Row alignmentHorizontal="end">
                        <Button type="secondary" disabled={this.state.submitting} onClick={this.onCancel}
                                data-testid="assign-routing-cancel-button">
                            {AppStrings.ASSIGN_ROUTING_CANCEL_BUTTON}
                        </Button>
                        <Button disabled={this.state.selectedDestination === '' || this.state.submitting}
                                onClick={this.onConfirm} data-testid="assign-routing-confirm-button">
                            {AppStrings.ASSIGN_ROUTING_CONFIRM_BUTTON}
                        </Button>
                    </Row>
                </Column>
            </Modal>
        );
    }
}

export default AssignRoutingModal;
